import { SettingType } from 'src/app/core/settings/settings.service';

/**
 * One field of a setting-type diff. `value` is the proposed/after value (normalized to a display
 * string), `previousValue` is the target/before value. `previousValue` is undefined when there is no
 * "before" side to compare against (a create), in which case `changed` is always false.
 */
export interface SettingTypeFieldDiff {
  fieldName: string;
  label: string;
  value: string;
  previousValue?: string;
  changed: boolean;
}

export function formatAllowableValues(values?: SettingType['allowableValues']): string {
  return values && values.length > 0 ? values.map(av => `${av.label}: ${av.value}`).join(', ') : '';
}

export function formatPlatforms(platforms?: string[]): string {
  // The hardware-platform field was added later, so legacy settings have it null/empty — and those
  // only ever applied to ERs. A null/empty list therefore means "ER only", not "all platforms".
  return platforms && platforms.length > 0 ? platforms.join(', ') : 'ER';
}

function buildField(fieldName: string, label: string, value: any, previousValue?: any): SettingTypeFieldDiff {
  const hasPrevious = previousValue !== undefined;
  const normalizedValue = value ?? '';
  const normalizedPrevious = previousValue ?? '';
  return {
    fieldName,
    label,
    value: String(normalizedValue),
    previousValue: hasPrevious ? String(normalizedPrevious) : undefined,
    changed: hasPrevious && String(normalizedValue) !== String(normalizedPrevious)
  };
}

/**
 * Compares a proposed setting-type definition against an optional previous one, producing one row per
 * relevant field. When `previous` is supplied, fields whose value differs are flagged `changed` so a
 * before→after diff can be rendered. When `previous` is undefined (a create), every row's
 * `previousValue` is undefined and nothing is flagged changed.
 *
 * Shared by the promote/approve modal (verification + diff) and the audit grid (historical diff) so
 * both compare the exact same field set with identical normalization.
 */
export function diffSettingTypes(proposed: SettingType, previous?: SettingType): SettingTypeFieldDiff[] {
  const fields: SettingTypeFieldDiff[] = [
    buildField('name', 'Name', proposed.name, previous?.name),
    buildField('label', 'Label', proposed.label, previous?.label),
    buildField('category', 'Category', proposed.category, previous?.category),
    buildField(
      'class',
      'Class',
      (proposed as any)?.['class'] || 'Free',
      previous ? ((previous as any)?.['class'] || 'Free') : undefined
    ),
    buildField('dataType', 'Data Type', proposed.dataType, previous?.dataType),
    buildField('defaultValue', 'Default Value', proposed.defaultValue, previous?.defaultValue)
  ];

  // Include a row when the field is present on either side, so both additions and removals show.
  if (proposed.minValue || previous?.minValue) {
    fields.push(buildField('minValue', 'Min Value', proposed.minValue, previous?.minValue));
  }

  if (proposed.maxValue || previous?.maxValue) {
    fields.push(buildField('maxValue', 'Max Value', proposed.maxValue, previous?.maxValue));
  }

  if (proposed.units || previous?.units) {
    fields.push(buildField('units', 'Units', proposed.units, previous?.units));
  }

  if ((proposed.allowableValues && proposed.allowableValues.length > 0) ||
      (previous?.allowableValues && previous.allowableValues.length > 0)) {
    fields.push(buildField(
      'allowableValues',
      'Allowable Values',
      formatAllowableValues(proposed.allowableValues),
      previous ? formatAllowableValues(previous.allowableValues) : undefined
    ));
  }

  fields.push(buildField(
    'applicableHardwarePlatforms',
    'Applicable Hardware Platforms',
    formatPlatforms(proposed.applicableHardwarePlatforms),
    previous ? formatPlatforms(previous.applicableHardwarePlatforms) : undefined
  ));

  return fields;
}
